import { useEffect, useState } from 'react';
import type { Arch } from '../components/download/types';

interface UADataLike {
  getHighEntropyValues?: (hints: string[]) => Promise<{
    architecture?: string;
    bitness?: string;
  }>;
}

interface ArchDetectionResult {
  arch: Arch | null;
  isLoading: boolean;
}

/**
 * Detects the user's CPU architecture (x64 Intel vs arm64 Apple Silicon).
 *
 * Chrome/Edge expose `navigator.userAgentData.getHighEntropyValues(["architecture"])`
 * which returns "arm" on Apple Silicon and "x86" on Intel. Safari and Firefox do
 * not implement this API, so on those browsers we return `null` and the UI falls
 * back to showing both options without a recommendation.
 */
export function useArchDetection(): ArchDetectionResult {
  const [arch, setArch] = useState<Arch | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const uaData = (navigator as unknown as { userAgentData?: UADataLike }).userAgentData;

    if (!uaData?.getHighEntropyValues) {
      setIsLoading(false);
      return;
    }

    uaData
      .getHighEntropyValues(['architecture', 'bitness'])
      .then((values) => {
        if (cancelled) return;
        const architecture = (values.architecture ?? '').toLowerCase();
        if (architecture === 'arm') {
          setArch('arm64');
        } else if (architecture === 'x86') {
          setArch('x64');
        } else {
          setArch(null);
        }
      })
      .catch(() => {
        if (!cancelled) setArch(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { arch, isLoading };
}
